import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { useApplicationEvent } from '../../../../../../../core/Analytics/useApplicationEvent';
import { useSourceAccountProvidedSkipsAis } from '../../../../../hooks/useSourceAccountProvidedSkipsAis/useSourceAccountProvidedSkipsAis';
import { PaymentProviderConditions } from '../../../../../models/payments';
import { useSelectedAuthenticationOptions } from '../../../../../../hooks/useSelectedAuthenticationOptions/useSelectedAuthenticationOptions';
import { SubmitCredentialsPayload } from '../../../../../../../components/screens/SubmitCredentials/hooks/useSubmitCredentials/useSubmitCredentials';
import { buildHomeUrl } from '../../../../../../../routes';
import { AppState } from '../../../../../../../stores/redux-store';
import { Provider, ProviderEntity } from '../../../../../../../models/provider';
import {
  LegacySubmitCredentialsScreen as LegacySubmitCredentialsScreenImport,
  SubmitCredentialsScreenOwnProps,
} from '../../../../../../../components/screens/molecules/SubmitCredentialsScreen/LegacySubmitCredentialsScreen';
import { useConfiguration } from '../../../../../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { onSubmitCredentialsPay as onSubmitCredentialsPayConnect } from '../../onSubmitCredentialsPay/onSubmitCredentialsPay';
import {
  ApplicationEvent,
  PageView,
  UserInteractionEvent,
} from '../../../../../../../models/analytics';
import { LegalDisclaimer } from '../../../../../../../components/molecules/LegalDisclaimer/LegalDisclaimer';
import {
  InteractionElement,
  ParentApplicationEvent,
  parentMessenger,
} from '../../../../../../../core/Analytics/ParentApplicationEventMessenger';
import { useProduct } from '../../../../../../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { LoadingOverlay } from '../../../../../../../core/screens/LoadingOverlay/LoadingOverlay';
import { ProviderRedirectInstructions } from '../../../../../../../core/screens/ProviderRedirectInstructions/ProviderRedirectInstructions';
import { PrimaryButton } from '../../../../../../../components/atoms/Button/Button';
import { messages as otherMessages } from '../../../../../../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingThirdpartyAuthenticationContainer/molecules/AwaitingThirdpartyAuthorization/messages';
import { messages as submitButtonMessages } from '../../../../../../../components/screens/SubmitCredentials/messages';
import { ConsentDisclaimer } from '../../../../molecules/ConsentDisclaimer/ConsentDisclaimer';
import { SubmitCredentialsScreen } from '../../../../../../../components/screens/molecules/SubmitCredentialsScreen/types';
import { SourceAccountSelectionDisclaimer } from './molecules/SourceAccountSelectionDisclaimer/SourceAccountSelectionDisclaimer';
import { AccountSelectionDisclaimer } from './molecules/AccountSelectionDisclaimer/AccountSelectionDisclaimer';
import { LoginTemplatePayScreen as LoginTemplatePayScreenImport } from './molecules/LoginTemplatePayScreen/LoginTemplatePayScreen';
import { messages } from './messages';

export type SubmitPayCredentialsProps = PropsFromRedux & {
  version: string;
  provider: ProviderEntity;
  footer?: React.ReactNode;
  onExecute: VoidFunction | undefined;
  onHeaderButtonClick: VoidFunction;
  onRedirectTo: (redirectTo: string) => void;
  isDesktop?: boolean;
  autoSubmitCredentials?: boolean;
  LoginTemplatePayScreen?: SubmitCredentialsScreen;
  LegacySubmitCredentialsScreen?: React.ComponentType<SubmitCredentialsScreenOwnProps>;
};

export const SubmitPayCredentialsPresenter: React.FC<SubmitPayCredentialsProps> = ({
  version,
  provider,
  session,
  footer,
  credentials,
  onExecute,
  userCredentials,
  userHintId,
  onSubmitCredentialsPay,
  onHeaderButtonClick,
  onRedirectTo,
  providerPaymentConditions,
  isDesktop = userAgent.isDesktop,
  autoSubmitCredentials = false,
  LoginTemplatePayScreen = LoginTemplatePayScreenImport,
  LegacySubmitCredentialsScreen = LegacySubmitCredentialsScreenImport,
}) => {
  const { analytics, sendEvent } = useApplicationEvent();
  const { productType, productDimension } = useProduct();
  const { data: configurationData } = useConfiguration();
  const selectedAuthenticationOptions = useSelectedAuthenticationOptions(provider);
  const canSkipAIS = useSourceAccountProvidedSkipsAis();

  const handleSubmitCredentials = ({ fields }: SubmitCredentialsPayload) => {
    analytics.sendInteractionEvent({
      action: `${PageView.SUBMIT_CREDENTIALS_SCREEN}/${UserInteractionEvent.SUBMIT_CREDENTIALS}`,
      view: PageView.SUBMIT_CREDENTIALS_SCREEN,
    });

    parentMessenger.fireInteractionEvent({
      pageView: PageView.SUBMIT_CREDENTIALS_SCREEN,
      element: InteractionElement.PRIMARY_BUTTON,
    });

    if (Provider.isOpenBanking(provider)) {
      sendEvent(
        ApplicationEvent.PROVIDER_AUTHENTICATION_INITIALIZED,
        ParentApplicationEvent.PROVIDER_AUTHENTICATION_INITIALIZED
      );
    } else {
      sendEvent(
        ApplicationEvent.CREDENTIALS_SUBMITTED,
        ParentApplicationEvent.CREDENTIALS_SUBMITTED
      );
    }

    const submitCredentialsPayload = {
      fields,
      configurationData,
      provider,
      userCredentials,
      credentials,
      providerPaymentConditions,
      canSkipAIS,
    };

    onSubmitCredentialsPay({
      ...submitCredentialsPayload,
      credentialsRequestBase: {
        ...selectedAuthenticationOptions,
      },
    });
    onExecute?.();
  };

  const handleHeaderButtonClick = () => {
    if (Provider.isUkOpenBanking(provider)) {
      onRedirectTo(buildHomeUrl({ version, productType, productDimension }));
    } else {
      onHeaderButtonClick();
    }
  };
  const isPISOnly =
    Boolean(providerPaymentConditions) &&
    !PaymentProviderConditions.isSourceAccountRequiredBeforeRedirect(providerPaymentConditions);
  const isAISAfterAggregationAccounts = Boolean(credentials);
  const showProviderInstructions =
    isDesktop &&
    Provider.isRedirectAuthenticationFlow(provider) &&
    !Provider.hasFields(provider) &&
    (isPISOnly || isAISAfterAggregationAccounts);

  if (showProviderInstructions) {
    return (
      <ProviderRedirectInstructions
        provider={provider}
        primaryButton={
          <PrimaryButton
            type="submit"
            color="primary"
            variant="contained"
            onClick={() => {
              handleSubmitCredentials({ fields: {} });
            }}
            pageView={PageView.SUBMIT_CREDENTIALS_SCREEN}
          >
            <FormattedMessage
              {...otherMessages.buttonText}
              values={{ providerName: provider.displayName }}
            />
          </PrimaryButton>
        }
        onBack={handleHeaderButtonClick}
        pageView={PageView.SUBMIT_CREDENTIALS_SCREEN}
        legalDisclaimer={
          <LegalDisclaimer provider={provider} pageView={PageView.SUBMIT_CREDENTIALS_SCREEN} />
        }
        instructionsTitle={
          <FormattedMessage
            {...messages.titleSign}
            values={{ providerName: provider.displayName }}
          />
        }
        userHintId={userHintId}
        footer={footer}
      />
    );
  }

  if (!autoSubmitCredentials) {
    const shouldUseLoginTemplate = Provider.hasLoginHeaderColour(provider);

    if (shouldUseLoginTemplate) {
      return (
        <LoginTemplatePayScreen onSubmit={handleSubmitCredentials} onBack={onHeaderButtonClick} />
      );
    }

    let bodyBlock = <ConsentDisclaimer />;
    let buttonLabel = <FormattedMessage {...submitButtonMessages.agree} />;

    if (isDesktop) {
      bodyBlock = <AccountSelectionDisclaimer />;
      buttonLabel = <FormattedMessage {...submitButtonMessages.continue} />;
    }

    if (session?.sourceAccountNumber) {
      bodyBlock = <SourceAccountSelectionDisclaimer />;
      buttonLabel = <FormattedMessage {...submitButtonMessages.continue} />;
    }

    return (
      <LegacySubmitCredentialsScreen
        provider={provider}
        renderProviderFields
        bodyBlock={bodyBlock}
        buttonLabel={buttonLabel}
        onBack={handleHeaderButtonClick}
        onSubmitCredentials={handleSubmitCredentials}
        legalDisclaimer={
          <LegalDisclaimer provider={provider} pageView={PageView.SUBMIT_CREDENTIALS_SCREEN} />
        }
      />
    );
  }

  return <LoadingOverlay />;
};

const mapStateToProps = (state: AppState) => {
  return {
    session: state.session.data,
    credentials: state.credentials.data,
    userCredentials: state.userCredentials.data,
    providerPaymentConditions: state.selectedProvider.data.paymentConditions,
    userHintId: state.user.idHint,
  };
};

const mapDispatchToProps = {
  onSubmitCredentialsPay: onSubmitCredentialsPayConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const SubmitPayCredentials = connector(SubmitPayCredentialsPresenter);
